import * as designTokens from '@calm/evexia-design-tokens';
import { createTheme } from '../index';
import { avenirFontFaces, createFontFace } from './font';
import { darkPalette, lightPalette } from './palette';
import typographyVariants from './typography';

declare module '@mui/material/styles' {
    interface TypographyVariants {
        srOnly: React.CSSProperties;
        body1Demi: React.CSSProperties;
        buttonLarge: React.CSSProperties;
        buttonSmall: React.CSSProperties;
        avatarLetter: React.CSSProperties;
        inputLabel: React.CSSProperties;
        inputText: React.CSSProperties;
        helperText: React.CSSProperties;
        tooltip: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        srOnly?: React.CSSProperties;
        body1Demi?: React.CSSProperties;
        buttonLarge?: React.CSSProperties;
        buttonSmall?: React.CSSProperties;
        avatarLetter?: React.CSSProperties;
        inputLabel?: React.CSSProperties;
        inputText?: React.CSSProperties;
        helperText?: React.CSSProperties;
        tooltip?: React.CSSProperties;
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        srOnly: true;
        body1Demi: true;
        buttonLarge: true;
        buttonSmall: true;
        avatarLetter: true;
        inputLabel: true;
        inputText: true;
        helperText: true;
        tooltip: true;
    }
}

// Utilities to create real theme with
const defaultTheme = createTheme({
    spacing: designTokens.DS_SHAPE_SPACING.BASE,
    breakpoints: {
        values: {
            xs: designTokens.DS_BREAKPOINT.INTRA,
            sm: designTokens.DS_BREAKPOINT.SMALL,
            md: designTokens.DS_BREAKPOINT.MEDIUM,
            lg: designTokens.DS_BREAKPOINT.LARGE,
            xl: designTokens.DS_BREAKPOINT.ULTRA,
        },
    },
});

export type ColorMode = 'light' | 'dark';

const focusedOutline = (colorMode: ColorMode) =>
    `${
        colorMode === 'light'
            ? designTokens.DS_COLOR.LIGHT_FOCUS_OUTLINE
            : designTokens.DS_COLOR.DARK_FOCUS_OUTLINE
    } auto 1px`;

const makeTheme = (
    requiredLabelText?: string,
    colorMode: ColorMode = 'light'
) =>
    createTheme({
        components: {
            MuiChip: {
                defaultProps: {
                    size: 'small',
                },
                styleOverrides: {
                    label: { ...typographyVariants.caption },
                },
            },
            MuiListItemText: {
                styleOverrides: {
                    primary: {
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_TEXT_BODY
                                : designTokens.DS_COLOR.DARK_TEXT_BODY,
                    },
                    secondary: {
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_TEXT_SUB
                                : designTokens.DS_COLOR.DARK_TEXT_SUB,
                    },
                },
            },
            MuiListItemButton: {
                styleOverrides: {
                    root: {
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_ACTION_ACTIVE
                                : designTokens.DS_COLOR.DARK_ACTION_ACTIVE,
                    },
                },
            },
            MuiLinearProgress: {
                styleOverrides: {
                    root: {
                        backgroundColor:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_ACTION_FOCUS
                                : designTokens.DS_COLOR.DARK_ACTION_FOCUS,
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    asterisk: {
                        // hiding the asterisk
                        fontSize: 0,
                        '&::after': {
                            marginLeft: defaultTheme.spacing(0.5),
                            content: requiredLabelText,
                            // restoring font size so that this shows
                            fontSize: '1rem',
                        },
                    },
                },
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true,
                    size: 'large',
                },
                styleOverrides: {
                    sizeLarge: {
                        height: designTokens.DS_HEIGHT.BUTTON_LARGE,
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.BUTTON_LARGE,
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_LARGE,
                        lineHeight: 1,
                    },
                    sizeMedium: {
                        height: designTokens.DS_HEIGHT.BUTTON_MEDIUM,
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.BUTTON_MEDIUM,
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_MEDIUM,
                        lineHeight: 1,
                    },
                    sizeSmall: {
                        height: designTokens.DS_HEIGHT.BUTTON_SMALL,
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.BUTTON_SMALL,
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_SMALL,
                        lineHeight: 1,
                    },
                },
            },
            MuiFab: {
                styleOverrides: {
                    sizeMedium: {
                        height: designTokens.DS_HEIGHT.BUTTON_LARGE,
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.BUTTON_LARGE,
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_LARGE,
                    },
                    sizeSmall: {
                        height: designTokens.DS_HEIGHT.BUTTON_SMALL,
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.BUTTON_SMALL,
                        fontSize: designTokens.DS_FONT_SIZE.BUTTON_SMALL,
                    },
                },
            },
            MuiTextField: {
                defaultProps: {
                    variant: 'filled',
                },
            },
            MuiFilledInput: {
                styleOverrides: {
                    input: {
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.MEDIUM,
                    },
                    root: {
                        borderRadius:
                            designTokens.DS_SHAPE_BORDER_RADIUS.MEDIUM,
                        backgroundColor:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_BACKGROUND_PAPER
                                : designTokens.DS_COLOR.BLACK_20P,
                        border: `1px solid ${
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_INPUT_LINE
                                : designTokens.DS_COLOR.DARK_INPUT_LINE
                        }`,
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_TEXT_BODY
                                : designTokens.DS_COLOR.DARK_TEXT_BODY,
                        '&.Mui-error': {
                            border: `1px solid ${
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR.LIGHT_ERROR_MAIN
                                    : designTokens.DS_COLOR.DARK_ERROR_MAIN
                            }`,
                            'input:focus-visible': {
                                outline: `${
                                    colorMode === 'light'
                                        ? designTokens.DS_COLOR.LIGHT_ERROR_MAIN
                                        : designTokens.DS_COLOR.DARK_ERROR_MAIN
                                } auto 2px`,
                            },
                        },
                        '&:not(.Mui-error)': {
                            'input:focus-visible': {
                                outline: `${
                                    colorMode === 'light'
                                        ? designTokens.DS_COLOR
                                              .LIGHT_PRIMARY_MAIN
                                        : designTokens.DS_COLOR
                                              .DARK_PRIMARY_MAIN
                                } auto 2px`,
                            },
                        },
                        '&.Mui-focused': {
                            backgroundColor:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR
                                          .LIGHT_BACKGROUND_PAPER
                                    : designTokens.DS_COLOR
                                          .DARK_BACKGROUND_PAPER,
                        },
                        // Get rid of underline
                        '&:before': {
                            border: '0 !important',
                        },
                        '&:after': {
                            border: '0 !important',
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        top: '2px',
                    },
                    filled: {
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_TEXT_BODY
                                : designTokens.DS_COLOR.DARK_TEXT_BODY,
                        '&.Mui-focused:not(.Mui-error)': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR.LIGHT_TEXT_BODY
                                    : designTokens.DS_COLOR.DARK_TEXT_BODY,
                        },
                    },
                    shrink: {
                        top: '0px',
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_TEXT_BODY
                                : designTokens.DS_COLOR.DARK_TEXT_BODY,
                    },
                },
            },
            MuiButtonBase: {
                defaultProps: {
                    disableRipple: true,
                },
                styleOverrides: {
                    root: {
                        '&:focus-visible': {
                            // makes it easy to see which input is focused when using keyboard navigation
                            outline: focusedOutline(colorMode),
                        },
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        borderTop:
                            colorMode === 'light'
                                ? `1px solid ${designTokens.DS_COLOR.LIGHT_DIVIDER}`
                                : 'none',
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        boxShadow: 'none',
                        justifyContent: 'space-between',
                        borderBottom:
                            colorMode === 'light'
                                ? `1px solid ${designTokens.DS_COLOR.LIGHT_DIVIDER}`
                                : 'none',
                        backgroundColor:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_BACKGROUND_DEFAULT
                                : designTokens.DS_COLOR
                                      .DARK_BACKGROUND_PRIMARY_START,
                        [`${defaultTheme.breakpoints.down(
                            'sm'
                        )} and (orientation: landscape)`]: {
                            minHeight: designTokens.DS_HEIGHT.TOOLBAR_MOBILE,
                        },
                        [defaultTheme.breakpoints.down('md')]: {
                            paddingLeft:
                                designTokens.DS_SHAPE_SPACING.SIDE_SMALL,
                            paddingRight:
                                designTokens.DS_SHAPE_SPACING.SIDE_SMALL,
                        },
                        [defaultTheme.breakpoints.between('md', 'lg')]: {
                            paddingLeft:
                                designTokens.DS_SHAPE_SPACING.SIDE_MEDIUM,
                            paddingRight:
                                designTokens.DS_SHAPE_SPACING.SIDE_MEDIUM,
                        },
                        [defaultTheme.breakpoints.up('lg')]: {
                            paddingLeft:
                                designTokens.DS_SHAPE_SPACING.SIDE_LARGE,
                            paddingRight:
                                designTokens.DS_SHAPE_SPACING.SIDE_LARGE,
                        },
                    },
                },
            },
            MuiDrawer: {
                styleOverrides: {
                    paper: {
                        background:
                            colorMode === 'light'
                                ? undefined
                                : designTokens.DS_GRADIENT
                                      .DARK_BACKGROUND_PRIMARY,
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    input: {
                        '&:focus-visible': {
                            // makes it easy to see which input is focused when using keyboard navigation
                            outline: focusedOutline(colorMode),
                        },
                    },
                },
            },
            MuiRadio: {
                styleOverrides: {
                    root: {
                        '& input:focus-visible+span': {
                            // makes it easy to see which input is focused when using keyboard navigation
                            outline: focusedOutline(colorMode),
                        },
                        '&.Mui-checked': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR.LIGHT_SECONDARY_DARK
                                    : designTokens.DS_COLOR.DARK_SECONDARY_DARK,
                        },
                    },
                },
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        '& input:focus-visible+span': {
                            // makes it easy to see which input is focused when using keyboard navigation
                            outline: focusedOutline(colorMode),
                        },
                        '&.Mui-checked': {
                            color:
                                colorMode === 'light'
                                    ? designTokens.DS_COLOR.LIGHT_SECONDARY_DARK
                                    : designTokens.DS_COLOR.DARK_SECONDARY_DARK,
                        },
                    },
                },
            },
            MuiFormHelperText: {
                styleOverrides: {
                    root: {
                        ...typographyVariants.helperText,
                    },
                },
            },
            MuiAvatar: {
                styleOverrides: {
                    root: {
                        fontSize: designTokens.DS_FONT_SIZE.AVATAR,
                        width: designTokens.DS_HEIGHT.AVATAR_SMALL,
                        height: designTokens.DS_HEIGHT.AVATAR_SMALL,
                        backgroundColor:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.DARK_BACKGROUND_PAPER
                                : designTokens.DS_COLOR.LIGHT_BACKGROUND_PAPER,
                    },
                },
            },
            MuiTooltip: {
                defaultProps: {
                    /**
                     * When the tooltip wraps a button that becomes disabled
                     * there is a bug that leaves the tooltip open. Disabling the
                     * focus listener as a workaround
                     */
                    disableFocusListener: true,
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        color:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_TEXT_SUB
                                : designTokens.DS_COLOR.DARK_TEXT_SUB,
                    },
                },
            },
            MuiPaper: {
                defaultProps: {
                    elevation: 0,
                },
                styleOverrides: {
                    root: {
                        borderRadius: designTokens.DS_SHAPE_BORDER_RADIUS.CARD,
                        backgroundColor:
                            colorMode === 'light'
                                ? designTokens.DS_COLOR.LIGHT_BACKGROUND_PAPER
                                : designTokens.DS_COLOR.BLACK_20P,
                    },
                },
            },
            MuiCssBaseline: {
                styleOverrides: avenirFontFaces
                    .map((fontFace) => createFontFace(fontFace))
                    .join('\n\n'),
            },
        },
        typography: {
            ...typographyVariants,

            fontFamily: designTokens.DS_FONT_FAMILY,
            htmlFontSize: designTokens.DS_FONT_SIZE.NORMAL_MEDIUM, // Same as MUI default
            fontSize: designTokens.DS_FONT_SIZE.NORMAL_SMALL, // Same as MUI default

            fontWeightBold: designTokens.DS_FONT_WEIGHT.BOLD,
            fontWeightMedium: designTokens.DS_FONT_WEIGHT.MEDIUM,
            fontWeightRegular: designTokens.DS_FONT_WEIGHT.REGULAR,
            fontWeightLight: designTokens.DS_FONT_WEIGHT.LIGHT,
        },
        spacing: designTokens.DS_SHAPE_SPACING.BASE,
        shape: {
            borderRadius: designTokens.DS_SHAPE_BORDER_RADIUS.SMALL,
        },
        palette: colorMode === 'light' ? lightPalette : darkPalette,
        breakpoints: {
            values: {
                xs: designTokens.DS_BREAKPOINT.INTRA,
                sm: designTokens.DS_BREAKPOINT.SMALL,
                md: designTokens.DS_BREAKPOINT.MEDIUM,
                lg: designTokens.DS_BREAKPOINT.LARGE,
                xl: designTokens.DS_BREAKPOINT.ULTRA,
            },
        },
    });

export default makeTheme;
